import axios from 'axios'
import {envVars} from '@/envVars'
import {LinearCreatedIssue, LinearIssue, LinearIssuePayload} from '../types'

const LINEAR_ENDPOINT = 'https://api.linear.app/graphql'

const PROJECT_ISSUES_QUERY = `
    query ProjectIssues($projectId: ID!) {
        issues(filter: {project: {id: {eq: $projectId}}}, first: 100, orderBy: createdAt) {
            nodes {
                id
                identifier
                title
                description
                url
                createdAt
                state {
                    name
                    color
                    type
                }
            }
        }
    }
`

const ISSUE_CREATE_MUTATION = `
    mutation IssueCreate($input: IssueCreateInput!) {
        issueCreate(input: $input) {
            success
            issue {
                id
                identifier
                title
                url
            }
        }
    }
`

const linearHeaders = () => ({
    'Content-Type': 'application/json',
    // Linear API keys must NOT use a Bearer prefix; OAuth tokens use Bearer.
    Authorization: envVars.VITE_LINEAR_API_KEY
})

type GraphQLResponse<T> = {
    data?: T
    errors?: Array<{message: string}>
}

const throwOnGraphQLError = <T>(response: GraphQLResponse<T>): T => {
    if (response.errors?.length) {
        throw new Error(response.errors.map(e => e.message).join('; '))
    }
    if (!response.data) {
        throw new Error('Linear API returned no data')
    }
    return response.data
}

export const linearIssueCreate = async (payload: LinearIssuePayload): Promise<LinearCreatedIssue> => {
    const response = await axios.post<GraphQLResponse<{issueCreate: {success: boolean; issue: LinearCreatedIssue}}>>(
        LINEAR_ENDPOINT,
        {query: ISSUE_CREATE_MUTATION, variables: {input: payload}},
        {headers: linearHeaders()}
    )
    const data = throwOnGraphQLError(response.data)
    if (!data.issueCreate.success) {
        throw new Error('Linear refused the issue creation')
    }
    return data.issueCreate.issue
}

export const linearProjectIssues = async (projectId: string): Promise<LinearIssue[]> => {
    const response = await axios.post<GraphQLResponse<{issues: {nodes: LinearIssue[]}}>>(
        LINEAR_ENDPOINT,
        {query: PROJECT_ISSUES_QUERY, variables: {projectId}},
        {headers: linearHeaders()}
    )
    const data = throwOnGraphQLError(response.data)
    // Linear returns issues ascending by createdAt; show the most recent first.
    return [...data.issues.nodes].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}
